import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientAPI, ipdAPI } from '../../utils/api';
import PatientCard from './PatientCard';

const statusBadge = (status) => {
    const map = {
        'Completed':  { bg: '#dcfce7', color: '#166534' },
        'Waiting':    { bg: '#dbeafe', color: '#1e40af' },
        'In Progress':{ bg: '#fef9c3', color: '#854d0e' },
        'Cancelled':  { bg: '#fee2e2', color: '#991b1b' },
        'No Show':    { bg: '#f3f4f6', color: '#374151' },
    };
    return map[status] || { bg: '#f3f4f6', color: '#374151' };
};

export default function PatientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [history, setHistory] = useState({ opd: [], lab: [], radiology: [], ipd: [], ae: [] });
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('opd');
    const [showCard, setShowCard] = useState(false);
    const [showReadmit, setShowReadmit] = useState(false);
    const [readmitSource, setReadmitSource] = useState(null);
    const [wards, setWards] = useState([]);
    const [availBeds, setAvailBeds] = useState([]);
    const [readmitForm, setReadmitForm] = useState({
        ward_id: '', bed_id: '', admitting_doctor: '', diagnosis: '',
        room_category: 'General', payment_mode: 'Cash', advance_paid: 0,
    });
    const [readmitSaving, setReadmitSaving] = useState(false);
    const [readmitMsg, setReadmitMsg] = useState('');

    useEffect(() => {
        Promise.all([
            patientAPI.getById(id),
            patientAPI.getHistory(id),
        ]).then(([p, h]) => {
            setPatient(p.data);
            setHistory(h.data);
        }).catch(() => navigate('/patients/search'))
          .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;
    if (!patient) return null;

    const Row = ({ label, value }) => (
        <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '10px 0' }}>
            <div style={{ width: 180, fontSize: 12, fontWeight: 600, color: '#64748b' }}>{label}</div>
            <div style={{ fontSize: 13, color: '#1e293b' }}>{value || '—'}</div>
        </div>
    );

    const tabs = [
        { key: 'opd',       label: `OPD (${history.opd.length})`,        color: '#1e3a5f' },
        { key: 'ipd',       label: `IPD (${history.ipd.length})`,         color: '#b45309' },
        { key: 'ae',        label: `A&E (${history.ae.length})`,          color: '#dc2626' },
        { key: 'lab',       label: `Lab (${history.lab.length})`,         color: '#7c3aed' },
        { key: 'radiology', label: `Radiology (${history.radiology.length})`, color: '#0369a1' },
        { key: 'info',      label: 'Patient Info',                        color: '#065f46' },
    ];

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => navigate('/patients/search')}
                    style={{ padding: '7px 14px', background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                    ← Back
                </button>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>Patient History</h2>
            </div>

            {/* Patient Header */}
            <div style={{ background: '#1e3a5f', borderRadius: 10, padding: 20, marginBottom: 20, color: 'white', display: 'flex', gap: 24, alignItems: 'center' }}>
                <div style={{ width: 64, height: 64, background: '#2d5a8e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>👤</div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{patient.full_name}</div>
                    <div style={{ fontSize: 14, color: '#93c5fd', marginTop: 4 }}>MRN: <strong>{patient.mrn}</strong></div>
                    <div style={{ fontSize: 13, color: '#93c5fd' }}>{patient.gender} | Age: {patient.age_years ? `${patient.age_years} yrs` : '—'} | Blood: {patient.blood_group || '—'} | Phone: {patient.phone}</div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 22, fontWeight: 800 }}>{history.opd.length}</div>
                        <div style={{ fontSize: 10, color: '#93c5fd' }}>OPD Visits</div>
                    </div>
                    <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 22, fontWeight: 800 }}>{history.ipd.length}</div>
                        <div style={{ fontSize: 10, color: '#93c5fd' }}>IPD Admits</div>
                    </div>
                    <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 22, fontWeight: 800 }}>{history.ae.length}</div>
                        <div style={{ fontSize: 10, color: '#93c5fd' }}>A&E Visits</div>
                    </div>
                    <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 22, fontWeight: 800 }}>{history.lab.length}</div>
                        <div style={{ fontSize: 10, color: '#93c5fd' }}>Lab Orders</div>
                    </div>
                    <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 22, fontWeight: 800 }}>{history.radiology.length}</div>
                        <div style={{ fontSize: 10, color: '#93c5fd' }}>Radiology</div>
                    </div>
                    <button onClick={() => setShowCard(true)}
                        style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <span style={{ fontSize: 18 }}>🪪</span>
                        <span style={{ fontSize: 10, color: '#93c5fd' }}>Print Card</span>
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{ padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: tab === t.key ? t.color : '#f1f5f9',
                            color: tab === t.key ? 'white' : '#374151' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* OPD Tab */}
            {tab === 'opd' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {history.opd.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No OPD visits found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Visit #', 'Date', 'Complaint', 'Diagnosis', 'Department', 'Doctor', 'Follow Up', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {history.opd.map((v, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#1e3a5f' }}>{v.visit_number}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{new Date(v.visit_date).toLocaleDateString('en-PK')}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.chief_complaint || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.diagnosis || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.department || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.doctor || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{v.follow_up_date ? new Date(v.follow_up_date).toLocaleDateString('en-PK') : '—'}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: statusBadge(v.status).bg, color: statusBadge(v.status).color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{v.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* IPD Tab */}
            {tab === 'ipd' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {history.ipd.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No IPD admissions found</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {history.ipd.map((a, i) => {
                                const isActive = a.status === 'Admitted';
                                const canReadmit = i === 0 && !isActive;
                                return (
                                    <div key={i} style={{ borderBottom: '1px solid #f1f5f9', padding: '14px 18px', background: isActive ? '#fff7ed' : 'white' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                <span style={{ fontWeight: 800, fontSize: 14, color: '#b45309' }}>{a.admission_number}</span>
                                                {isActive && <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>CURRENTLY ADMITTED</span>}
                                                {a.discharge_type && <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Discharged: {a.discharge_type}</span>}
                                            </div>
                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                {canReadmit && (
                                                    <button
                                                        onClick={async () => {
                                                            setReadmitSource(a);
                                                            setReadmitForm({
                                                                ward_id: a.ward_id || '',
                                                                bed_id: '',
                                                                admitting_doctor: a.admitting_doctor || '',
                                                                diagnosis: a.diagnosis || '',
                                                                room_category: a.room_category || 'General',
                                                                payment_mode: a.payment_mode || 'Cash',
                                                                advance_paid: 0,
                                                            });
                                                            setReadmitMsg('');
                                                            try {
                                                                const wR = await ipdAPI.getWards();
                                                                setWards(wR.data || []);
                                                                if (a.ward_id) {
                                                                    const bR = await ipdAPI.getAllBeds(a.ward_id);
                                                                    setAvailBeds((bR.data || []).filter(b => b.status === 'Available'));
                                                                }
                                                            } catch {}
                                                            setShowReadmit(true);
                                                        }}
                                                        style={{ padding: '5px 14px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                        Re-admit
                                                    </button>
                                                )}
                                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                                    Admitted: {new Date(a.admission_date).toLocaleDateString('en-PK')}
                                                    {a.discharge_date && ` — Discharged: ${new Date(a.discharge_date).toLocaleDateString('en-PK')}`}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, fontSize: 12 }}>
                                            <div><span style={{ color: '#64748b' }}>Diagnosis: </span><strong>{a.diagnosis || '—'}</strong></div>
                                            <div><span style={{ color: '#64748b' }}>Ward: </span><strong>{a.ward_name || '—'}</strong></div>
                                            <div><span style={{ color: '#64748b' }}>Doctor: </span><strong>{a.admitting_doctor || '—'}</strong></div>
                                            <div><span style={{ color: '#64748b' }}>Room: </span><strong>{a.room_category || '—'}</strong></div>
                                            <div><span style={{ color: '#64748b' }}>Referring Dr: </span>{a.referring_doctor || '—'}</div>
                                            <div><span style={{ color: '#64748b' }}>Expected Stay: </span>{a.expected_los ? `${a.expected_los} days` : '—'}</div>
                                            <div><span style={{ color: '#64748b' }}>Advance Paid: </span>{a.advance_paid ? `Rs. ${Number(a.advance_paid).toLocaleString()}` : '—'}</div>
                                            <div><span style={{ color: '#64748b' }}>Payment: </span>{a.payment_mode || '—'}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* A&E Tab */}
            {tab === 'ae' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {history.ae.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No A&E visits found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#fef2f2' }}>
                                    {['A&E #', 'Arrival', 'Triage', 'Mode', 'Doctor', 'Disposition', 'MLC', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#374151', borderBottom: '1px solid #fecaca' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {history.ae.map((v, i) => {
                                    const triageColors = { RED:'#dc2626', ORANGE:'#ea580c', YELLOW:'#ca8a04', GREEN:'#16a34a', BLUE:'#2563eb' };
                                    const tc = triageColors[v.triage_color] || '#64748b';
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#dc2626' }}>{v.ae_number}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{new Date(v.arrival_time).toLocaleString('en-PK')}</td>
                                            <td style={{ padding: '10px 14px' }}>
                                                {v.triage_category ? <span style={{ background: tc+'20', color: tc, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{v.triage_category}</span> : '—'}
                                            </td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.arrival_mode || '—'}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.doctor || '—'}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>{v.disposition || 'Active'}</td>
                                            <td style={{ padding: '10px 14px', fontSize: 12 }}>
                                                {v.is_mlc ? <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>MLC — {v.mlc_type || 'Yes'}</span> : '—'}
                                            </td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <span style={{ background: statusBadge(v.status).bg, color: statusBadge(v.status).color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{v.status}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Lab Tab */}
            {tab === 'lab' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {history.lab.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>No lab orders found</div>
                    ) : history.lab.map((o, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                <div>
                                    <span style={{ fontWeight: 700, color: '#7c3aed', fontSize: 13 }}>{o.order_number}</span>
                                    <span style={{ marginLeft: 12, fontSize: 12, color: '#64748b' }}>{new Date(o.created_at).toLocaleDateString('en-PK')}</span>
                                    <span style={{ marginLeft: 12, fontSize: 11, background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>{o.source}</span>
                                    <span style={{ marginLeft: 6, fontSize: 11, background: o.priority === 'STAT' ? '#fee2e2' : '#f3f4f6', color: o.priority === 'STAT' ? '#dc2626' : '#374151', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{o.priority}</span>
                                </div>
                                <span style={{ fontSize: 11, background: statusBadge(o.status).bg, color: statusBadge(o.status).color, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{o.status}</span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {(o.tests || []).filter(t => t.test).map((t, j) => (
                                    <span key={j} style={{ background: '#f5f3ff', color: '#7c3aed', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600 }}>
                                        {t.test}
                                        {t.result && <span style={{ color: t.flag === 'H' || t.flag === 'L' ? '#dc2626' : '#059669', marginLeft: 6 }}>{t.result} {t.unit}</span>}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Radiology Tab */}
            {tab === 'radiology' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {history.radiology.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No radiology orders found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Order #', 'Date', 'Modality', 'Study', 'Referred By', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {history.radiology.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#0369a1' }}>{r.order_number}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString('en-PK')}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600 }}>{r.modality}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{r.body_part}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{r.referred_by || '—'}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{r.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Patient Info Tab */}
            {tab === 'info' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                    <div style={{ background: 'white', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Personal Info</h3>
                        <Row label="Full Name" value={patient.full_name} />
                        <Row label="Father / Husband" value={patient.father_name} />
                        <Row label="CNIC / B-Form" value={patient.cnic} />
                        <Row label="Date of Birth" value={patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString('en-PK') : null} />
                        <Row label="Gender" value={patient.gender} />
                        <Row label="Blood Group" value={patient.blood_group} />
                        <Row label="Marital Status" value={patient.marital_status} />
                        <Row label="Nationality" value={patient.nationality} />
                        <Row label="Occupation" value={patient.occupation} />
                        <Row label="Patient Category" value={patient.patient_category} />
                        <Row label="Patient Type" value={patient.patient_type} />
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Contact Info</h3>
                        <Row label="Phone" value={patient.phone} />
                        <Row label="Alt Phone" value={patient.phone_alt} />
                        <Row label="Address" value={patient.address} />
                        <Row label="City" value={patient.city} />
                        <Row label="Province" value={patient.province} />
                        <Row label="Emergency Contact" value={patient.emergency_contact_name} />
                        <Row label="Emergency Phone" value={patient.emergency_contact_phone} />
                        <Row label="Emergency CNIC" value={patient.emergency_contact_cnic} />
                        <Row label="Relation" value={patient.emergency_contact_relation} />
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Medical Info</h3>
                        <Row label="Allergies" value={patient.allergies} />
                        <Row label="Chronic Conditions" value={patient.chronic_conditions} />
                        {patient.is_disabled && <Row label="Disability" value={patient.disability_type || 'Yes'} />}
                        <Row label="Remarks" value={patient.remarks} />
                        <Row label="Registered On" value={patient.registration_date ? new Date(patient.registration_date).toLocaleString('en-PK') : null} />
                        <Row label="MRN" value={patient.mrn} />
                    </div>
                </div>
            )}

            {showCard && <PatientCard patient={patient} onClose={() => setShowCard(false)} />}

            {/* Re-admit Modal */}
            {showReadmit && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 520, maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1e3a5f', marginBottom: 4 }}>Re-admit Patient</h3>
                        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 18 }}>MRN: {patient.mrn} — {patient.full_name}</p>

                        {readmitMsg && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{readmitMsg}</div>}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Ward *</label>
                                <select
                                    value={readmitForm.ward_id}
                                    onChange={async e => {
                                        const wid = e.target.value;
                                        setReadmitForm(f => ({ ...f, ward_id: wid, bed_id: '' }));
                                        if (wid) {
                                            try {
                                                const bR = await ipdAPI.getAllBeds(wid);
                                                setAvailBeds((bR.data || []).filter(b => b.status === 'Available'));
                                            } catch { setAvailBeds([]); }
                                        } else { setAvailBeds([]); }
                                    }}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                                    <option value="">Select Ward</option>
                                    {wards.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Available Bed *</label>
                                <select
                                    value={readmitForm.bed_id}
                                    onChange={e => setReadmitForm(f => ({ ...f, bed_id: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                                    <option value="">Select Bed</option>
                                    {availBeds.map(b => <option key={b.id} value={b.id}>{b.bed_number}</option>)}
                                </select>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Admitting Doctor *</label>
                                <input
                                    value={readmitForm.admitting_doctor}
                                    onChange={e => setReadmitForm(f => ({ ...f, admitting_doctor: e.target.value }))}
                                    placeholder="Doctor name"
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Diagnosis</label>
                                <input
                                    value={readmitForm.diagnosis}
                                    onChange={e => setReadmitForm(f => ({ ...f, diagnosis: e.target.value }))}
                                    placeholder="Diagnosis"
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Room Category</label>
                                <select
                                    value={readmitForm.room_category}
                                    onChange={e => setReadmitForm(f => ({ ...f, room_category: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                                    {['General', 'Semi-Private', 'Private', 'ICU', 'NICU', 'CCU'].map(c => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Payment Mode</label>
                                <select
                                    value={readmitForm.payment_mode}
                                    onChange={e => setReadmitForm(f => ({ ...f, payment_mode: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                                    {['Cash', 'Panel', 'Insurance', 'Government'].map(m => <option key={m}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Advance Paid (Rs.)</label>
                                <input
                                    type="number"
                                    value={readmitForm.advance_paid}
                                    onChange={e => setReadmitForm(f => ({ ...f, advance_paid: e.target.value }))}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                            </div>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                            Previous admission: <strong>{readmitSource?.admission_number}</strong> — Ward: <strong>{readmitSource?.ward_name || '—'}</strong> — Same MRN will be kept.
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowReadmit(false)}
                                style={{ padding: '9px 20px', background: '#f1f5f9', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
                                Cancel
                            </button>
                            <button
                                disabled={readmitSaving}
                                onClick={async () => {
                                    if (!readmitForm.ward_id || !readmitForm.bed_id || !readmitForm.admitting_doctor) {
                                        setReadmitMsg('Ward, bed, and doctor are required.');
                                        return;
                                    }
                                    setReadmitSaving(true);
                                    setReadmitMsg('');
                                    try {
                                        const token = localStorage.getItem('token');
                                        const payload = {
                                            patient_id: patient.id,
                                            ward_id: parseInt(readmitForm.ward_id),
                                            bed_id: parseInt(readmitForm.bed_id),
                                            admitting_doctor: readmitForm.admitting_doctor,
                                            diagnosis: readmitForm.diagnosis,
                                            room_category: readmitForm.room_category,
                                            payment_mode: readmitForm.payment_mode,
                                            advance_paid: parseFloat(readmitForm.advance_paid) || 0,
                                            admission_type: 'Re-admission',
                                        };
                                        const res = await ipdAPI.admitPatient(payload);
                                        setShowReadmit(false);
                                        navigate(`/ipd/patient/${res.data.id || res.data.admission?.id}`);
                                    } catch (err) {
                                        setReadmitMsg(err.response?.data?.message || 'Re-admission failed. Try again.');
                                    } finally {
                                        setReadmitSaving(false);
                                    }
                                }}
                                style={{ padding: '9px 24px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: readmitSaving ? 0.6 : 1 }}>
                                {readmitSaving ? 'Admitting...' : 'Confirm Re-admission'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
